import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Download, FileText, Share2, MessageSquare, X } from 'lucide-react';
import { compartilhamentoApi } from '@/services/portal-api';
import { StatusBadge } from '@/components/portal/StatusBadge';
import { CabecalhoIdentificacao } from '@/components/apreciacao/cabecalho-identificacao';
import { InformacoesMaquina } from '@/components/apreciacao/informacoes-maquina';
import { TabelaRiscos } from '@/components/apreciacao/tabela-riscos';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';
import type { Variaveis } from '@/lib/i18n';

type ExtraFieldMap = Record<string, string>;

/** Assinatura do `t` do `useI18n`, repassado às funções puras deste arquivo. */
type Traduzir = (chave: string, variaveis?: Variaveis) => string;

const KNOWN_MACHINE_FIELDS = new Set([
  'nome',
  'modelo',
  'fabricante',
  'site',
  'area',
  'local',
  'serie',
  'ano',
]);

const KNOWN_ATIVO_FIELDS = new Set(['id', 'equipamento', 'tag', 'area', 'local', 'tipo']);

/** Valores de status gravados no banco, na ordem em que aparecem no select. */
const STATUS_APRECIACAO = [
  'rascunho',
  'em_revisao',
  'aprovado_internamente',
  'aprovado',
  'a_tramitar',
  'tramitado',
  'tramitado_apos_revisao',
  'arquivado',
];

const KNOWN_SNAPSHOT_MACHINE_FIELDS = new Set([
  'nomeMaquina',
  'tipoMaquina',
  'identificacaoMaquina',
  'setorMaquina',
  'funcaoMaquina',
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const firstNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
};

const firstFiniteNumber = (...values: unknown[]): number | undefined => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return undefined;
};

const formatExtraFieldValue = (value: unknown, t: Traduzir): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value === 'boolean') return value ? t('comum.rotulo.sim') : t('comum.rotulo.nao');
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    const allSimple = value.every((item) => ['string', 'number', 'boolean'].includes(typeof item));
    if (allSimple) {
      const formatted = value
        .map((item) =>
          typeof item === 'boolean' ? (item ? t('comum.rotulo.sim') : t('comum.rotulo.nao')) : String(item)
        )
        .join(', ')
        .trim();
      return formatted.length > 0 ? formatted : null;
    }
    return JSON.stringify(value);
  }
  if (isRecord(value)) return JSON.stringify(value);
  return String(value);
};

const collectExtraFields = (apreciacao: any, t: Traduzir): ExtraFieldMap => {
  const extras: ExtraFieldMap = {};
  const machine = isRecord(apreciacao?.maquina) ? apreciacao.maquina : {};
  const ativo = isRecord(apreciacao?.ativo) ? apreciacao.ativo : {};

  const pushExtra = (key: string, rawValue: unknown) => {
    const formatted = formatExtraFieldValue(rawValue, t);
    if (formatted) extras[key] = formatted;
  };

  for (const [key, value] of Object.entries(machine)) {
    if (KNOWN_MACHINE_FIELDS.has(key)) continue;
    pushExtra(`maquina.${key}`, value);
  }

  for (const [key, value] of Object.entries(ativo)) {
    if (KNOWN_ATIVO_FIELDS.has(key)) continue;
    pushExtra(`ativo.${key}`, value);
  }

  for (const [key, value] of Object.entries(apreciacao || {})) {
    if (!/maquina/i.test(key)) continue;
    if (KNOWN_SNAPSHOT_MACHINE_FIELDS.has(key)) continue;
    if (key === 'maquinaId') continue;
    if (isRecord(value) || Array.isArray(value)) continue;
    pushExtra(`apreciacao.${key}`, value);
  }

  return extras;
};

const normalizeApreciacaoForView = (apreciacao: any, t: Traduzir) => {
  const maquina = isRecord(apreciacao?.maquina) ? apreciacao.maquina : {};
  const ativo = isRecord(apreciacao?.ativo) ? apreciacao.ativo : {};

  const nomeMaquina = firstNonEmptyString(apreciacao?.nomeMaquina, maquina.nome, ativo.equipamento);
  const tipoMaquina = firstNonEmptyString(apreciacao?.tipoMaquina, maquina.tipo, ativo.tipo);
  const identificacaoMaquina = firstNonEmptyString(apreciacao?.identificacaoMaquina, ativo.tag);
  const setorMaquina = firstNonEmptyString(apreciacao?.setorMaquina, maquina.area, ativo.area);
  const funcaoMaquina = firstNonEmptyString(apreciacao?.funcaoMaquina);

  return {
    dadosCabecalho: {
      numero: apreciacao.numero,
      numeroArcelorMittal: apreciacao.numeroArcelorMittal,
      revisao: apreciacao.revisao,
      numeroEmitente: apreciacao.numeroEmitente,
      emitente: apreciacao.emitente,
      emitirCREA: apreciacao.emitirCREA,
      codigoInterno: apreciacao.codigoInterno,
      titulo: apreciacao.titulo,
      maquina: nomeMaquina,
      ativoId: apreciacao.ativoId,
      ativo: apreciacao.ativo,
      dataEmissao: apreciacao.dataEmissao,
      statusLiberacao: apreciacao.statusLiberacao,
      nomeMaquina,
      tipoMaquina,
      identificacaoMaquina,
      setorMaquina,
      numeroDocumento: apreciacao.numeroDocumento,
    },
    dadosMaquina: {
      maquina: {
        nome: nomeMaquina,
        modelo: firstNonEmptyString(maquina.modelo),
        fabricante: firstNonEmptyString(maquina.fabricante),
        site: firstNonEmptyString(maquina.site),
        area: setorMaquina || firstNonEmptyString(maquina.area, ativo.area),
        local: firstNonEmptyString(maquina.local, ativo.local),
        serie: firstNonEmptyString(maquina.serie),
        ano: firstFiniteNumber(maquina.ano),
      },
      operadorEntrevistado: apreciacao.operadorEntrevistado,
      cargoOperador: apreciacao.cargoOperador,
      experiencia: apreciacao.experiencia,
      turnosTrabalho: apreciacao.turnosTrabalho,
      frequenciaUso: apreciacao.frequenciaUso,
      modoOperacao: apreciacao.modoOperacao,
      historicoAcidentes: apreciacao.historicoAcidentes,
      limitesUso: apreciacao.limitesUso,
      limitesEspaco: apreciacao.limitesEspaco,
      limitesAdicionais: apreciacao.limitesAdicionais,
      limitesAmbientais: apreciacao.limitesAmbientais,
      limitacoes: apreciacao.limitacoes,
      mauUsoRazoavelmentePrevisivel: apreciacao.mauUsoRazoavelmentePrevisivel || [],
      normasTecnicasReferencia: apreciacao.normasTecnicasReferencia || [],
      nota1: apreciacao.nota1,
      nota2: apreciacao.nota2,
      observacoesEntrevista: apreciacao.observacoesEntrevista,
      dataEntrevista: apreciacao.dataEntrevista,
      frequenciaAcessoOperacao: apreciacao.frequenciaAcessoOperacao,
      frequenciaAcessoManutencao: apreciacao.frequenciaAcessoManutencao,
      comprimentoHorizontal: apreciacao.comprimentoHorizontal,
      elevacao: apreciacao.elevacao,
      capacidadeCorreia: apreciacao.capacidadeCorreia,
      larguraCorreia: apreciacao.larguraCorreia,
      velocidadeCorreia: apreciacao.velocidadeCorreia,
      material: apreciacao.material,
      contraRecuo: apreciacao.contraRecuo,
      freio: apreciacao.freio,
      potencia: apreciacao.potencia,
      tensaoAcionamento: apreciacao.tensaoAcionamento,
      tensaoComando: apreciacao.tensaoComando,
      funcaoMaquina,
    },
    extraFields: collectExtraFields(apreciacao, t),
  };
};

export function ApreciacaoView() {
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [apreciacao, setApreciacao] = useState<any>(null);
  const [mostrarModalTemplate, setMostrarModalTemplate] = useState(false);
  const [nomeTemplate, setNomeTemplate] = useState('');
  const [descricaoTemplate, setDescricaoTemplate] = useState('');

  // Compartilhamento com Cliente
  const [mostrarModalCompartilhar, setMostrarModalCompartilhar] = useState(false);
  const [empresas, setEmpresas] = useState<{ id: string; nome: string }[]>([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState('');
  const [compartilhamentos, setCompartilhamentos] = useState<any[]>([]);
  const [, setLoadingCompartilhamentos] = useState(false);

  // Carregar compartilhamentos
  const carregarCompartilhamentos = async () => {
    if (!id) return;
    setLoadingCompartilhamentos(true);
    try {
      const res = await compartilhamentoApi.listar(id);
      if (res.success && res.data) setCompartilhamentos(res.data);
    } finally {
      setLoadingCompartilhamentos(false);
    }
  };

  const handleCompartilhar = async () => {
    if (!id || !empresaSelecionada) return;
    const res = await compartilhamentoApi.compartilhar(id, empresaSelecionada);
    if (res.success) {
      setMostrarModalCompartilhar(false);
      setEmpresaSelecionada('');
      carregarCompartilhamentos();
    } else {
      alert(res.message || t('apreciacoes.ver.erroCompartilhar'));
    }
  };

  const handleRevogar = async (empresaId: string) => {
    if (!id || !confirm(t('apreciacoes.ver.confirmarRevogar'))) return;
    const res = await compartilhamentoApi.revogar(id, empresaId);
    if (res.success) carregarCompartilhamentos();
  };

  useEffect(() => {
    if (!id) return;
    let mounted = true;

    const carregarApreciacao = async () => {
      setLoading(true);
      try {
        const response = await apiService.buscarApreciacao(id);
        if (!mounted) return;
        if (response.success && response.data) {
          setApreciacao(response.data);
          apiService.buscarRiscosImagens(id)
            .then((imagensRes) => {
              if (!mounted) return;
              if (imagensRes.success && imagensRes.data && Object.keys(imagensRes.data).length > 0) {
                setApreciacao((prev: any) =>
                  prev
                    ? {
                      ...prev,
                      riscos: (prev.riscos || []).map((r: any) => {
                        const imgs = imagensRes.data![r.id];
                        if (!imgs) return r;
                        return {
                          ...r,
                          fotoAtualUrl1: imgs.fotoAtualUrl1 ?? r.fotoAtualUrl1,
                          fotoAtualUrl2: imgs.fotoAtualUrl2 ?? r.fotoAtualUrl2,
                          fotoAtualUrl3: imgs.fotoAtualUrl3 ?? r.fotoAtualUrl3,
                          fotoAtualUrl4: imgs.fotoAtualUrl4 ?? r.fotoAtualUrl4,
                          fotoPlrUrl: imgs.fotoPlrUrl ?? r.fotoPlrUrl,
                        };
                      }),
                    }
                    : prev
                );
              }
            })
            .catch((err) => console.error('Erro ao carregar imagens dos riscos:', err));
        } else {
          alert(
            t('apreciacoes.ver.erroCarregarDetalhe', {
              detalhe: response.message || t('apreciacoes.erroDesconhecido'),
            })
          );
        }
      } catch (error) {
        if (!mounted) return;
        console.error('Erro ao carregar apreciação:', error);
        alert(t('apreciacoes.ver.erroCarregar'));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    carregarApreciacao();
    carregarCompartilhamentos();
    // Carregar empresas para o modal de compartilhamento
    apiService.listarEmpresas({}).then(res => {
      if (mounted && res.success && res.data) {
        setEmpresas(Array.isArray(res.data) ? res.data : []);
      }
    });
    return () => { mounted = false; };
  }, [id]);

  // Hooks ANTES de qualquer early return (regra dos hooks do React)
  const normalized = useMemo(
    () => (apreciacao ? normalizeApreciacaoForView(apreciacao, t) : null),
    [apreciacao, t]
  );

  const riscosFormatados = useMemo(() => (apreciacao?.riscos || []).map((risco: any) => ({
    id: risco.id,
    numero: risco.numero,
    parte: risco.parte,
    hrnAtual: risco.hrnAtual,
    classificacaoRisco: risco.classificacaoRisco,
    plr: risco.plr,
    sil: risco.sil,
    categoriaSeguranca: risco.categoriaSeguranca,
    tipoPerigo: risco.tipoPerigo,
    categoria: risco.categoria,
    conformeAtual: risco.conformeAtual,
  })), [apreciacao?.riscos]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500">{t('apreciacoes.ver.carregando')}</p>
        </div>
      </div>
    );
  }

  if (!apreciacao || !normalized) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('apreciacoes.ver.naoEncontrada')}</p>
        <Link
          to="/apreciacoes"
          className="mt-4 inline-block text-blue-600 hover:text-blue-800"
        >
          {t('apreciacoes.ver.voltarLista')}
        </Link>
      </div>
    );
  }

  const handleCriarTemplate = async () => {
    if (!id) return;

    if (!nomeTemplate.trim()) {
      alert(t('apreciacoes.ver.modalTemplate.nomeObrigatorio'));
      return;
    }

    try {
      const response = await apiService.criarTemplateDeApreciacao(id, {
        nome: nomeTemplate,
        descricao: descricaoTemplate || undefined
      });

      if (response.success) {
        alert(t('apreciacoes.ver.modalTemplate.criado'));
        setMostrarModalTemplate(false);
        setNomeTemplate('');
        setDescricaoTemplate('');
        navigate('/meus-templates');
      } else {
        alert(
          t('apreciacoes.ver.modalTemplate.erroCriarDetalhe', {
            detalhe: response.message || t('apreciacoes.erroDesconhecido'),
          })
        );
      }
    } catch (error) {
      console.error('Erro ao criar template:', error);
      alert(t('apreciacoes.ver.modalTemplate.erroCriar'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-0">
        <div className="flex items-center">
          <Link
            to="/apreciacoes"
            className="mr-4 p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {apreciacao.titulo || t('apreciacoes.ver.tituloFallback', { numero: apreciacao.numero })}
              </h1>
              <select
                value={apreciacao.status || 'rascunho'}
                onChange={async (e) => {
                  const novoStatus = e.target.value;
                  const res = await apiService.atualizarApreciacao(id!, { status: novoStatus });
                  if (res.success) {
                    setApreciacao((prev: any) => prev ? { ...prev, status: novoStatus } : prev);
                  } else {
                    alert(res.message || t('apreciacoes.ver.erroAlterarStatus'));
                  }
                }}
                className={`text-xs font-bold px-2.5 py-1 rounded-full border-0 cursor-pointer ${
                  apreciacao.status === 'rascunho' ? 'bg-gray-100 text-gray-700' :
                  apreciacao.status === 'em_revisao' ? 'bg-yellow-100 text-yellow-800' :
                  apreciacao.status === 'aprovado_internamente' ? 'bg-teal-100 text-teal-800' :
                  apreciacao.status === 'aprovado' ? 'bg-green-100 text-green-800' :
                  apreciacao.status === 'a_tramitar' ? 'bg-indigo-100 text-indigo-800' :
                  apreciacao.status === 'tramitado' ? 'bg-purple-100 text-purple-800' :
                  apreciacao.status === 'tramitado_apos_revisao' ? 'bg-violet-100 text-violet-800' :
                  apreciacao.status === 'arquivado' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-700'
                }`}
              >
                {STATUS_APRECIACAO.map((chave) => (
                  <option key={chave} value={chave}>
                    {t(`apreciacoes.status.opcao.${chave}`)}
                  </option>
                ))}
              </select>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {t('apreciacoes.ver.numeroRevisao', {
                numero: apreciacao.numero,
                revisao: apreciacao.revisao,
              })}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:space-x-3 w-full sm:w-auto">
          <button
            onClick={() => setMostrarModalTemplate(true)}
            className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <FileText className="h-4 w-4 mr-2" />
            {t('apreciacoes.ver.criarTemplate')}
          </button>
          <button className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
            <Download className="h-4 w-4 mr-2" />
            {t('apreciacoes.ver.exportarPdf')}
          </button>
          <button
            onClick={() => setMostrarModalCompartilhar(true)}
            className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-green-300 rounded-md shadow-sm text-sm font-medium text-green-700 bg-white hover:bg-green-50"
          >
            <Share2 className="h-4 w-4 mr-2" />
            {t('apreciacoes.ver.compartilhar')}
          </button>
          <button
            onClick={() => navigate(`/apreciacoes/${id}/editar`)}
            className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <Edit className="h-4 w-4 mr-2" />
            {t('comum.acao.editar')}
          </button>
        </div>
      </div>

      {/* 1. Cabeçalho / Identificação Documental */}
      <CabecalhoIdentificacao
        dados={normalized.dadosCabecalho}
        modoEdicao={false}
      />

      {/* 2. Informações Gerais da Máquina */}
      <InformacoesMaquina
        dados={normalized.dadosMaquina}
        extraFields={normalized.extraFields}
        modoEdicao={false}
      />

      {/* 3. Tabela Geral das Partes */}
      <TabelaRiscos
        riscos={riscosFormatados}
        modoEdicao={false}
      />

      {/* Secao: Compartilhamento com Cliente */}
      {compartilhamentos.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Share2 className="w-5 h-5 text-green-600" />
            {t('apreciacoes.ver.compartilhamento.titulo')}
          </h2>
          <div className="space-y-3">
            {compartilhamentos.map((c: any) => (
              <div key={c.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{c.empresa?.nome}</span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <StatusBadge status={c.status} />
                      <span className="text-xs text-gray-500">
                        {t('apreciacoes.ver.compartilhamento.anotacoes', {
                          total: c._count?.anotacoes || 0,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {(c._count?.anotacoes || 0) > 0 && (
                    <button
                      onClick={async () => {
                        const res = await compartilhamentoApi.listarAnotacoes(id!);
                        if (res.success) {
                          alert(
                            t('apreciacoes.ver.compartilhamento.anotacoesEncontradas', {
                              total: (res.data as any[])?.length || 0,
                            })
                          );
                        }
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      {t('apreciacoes.ver.compartilhamento.verAnotacoes')}
                    </button>
                  )}
                  <button
                    onClick={() => handleRevogar(c.empresa?.id)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                  >
                    <X className="w-3.5 h-3.5" />
                    {t('apreciacoes.ver.compartilhamento.revogar')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal Compartilhar com Cliente */}
      {mostrarModalCompartilhar && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-sm rounded-md bg-white dark:bg-gray-800">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('apreciacoes.ver.modalCompartilhar.titulo')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('comum.rotulo.empresa')}</label>
                <select
                  value={empresaSelecionada}
                  onChange={(e) => setEmpresaSelecionada(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="">{t('apreciacoes.ver.modalCompartilhar.selecioneEmpresa')}</option>
                  {empresas.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.nome}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => { setMostrarModalCompartilhar(false); setEmpresaSelecionada(''); }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                {t('comum.acao.cancelar')}
              </button>
              <button
                onClick={handleCompartilhar}
                disabled={!empresaSelecionada}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                <Share2 className="h-4 w-4 mr-2" />
                {t('apreciacoes.ver.compartilhar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Criar Template */}
      {mostrarModalTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-sm rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('apreciacoes.ver.modalTemplate.titulo')}</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('apreciacoes.ver.modalTemplate.nome')}</label>
                  <input
                    type="text"
                    value={nomeTemplate}
                    onChange={(e) => setNomeTemplate(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder={t('apreciacoes.ver.modalTemplate.nomePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('comum.rotulo.descricao')}</label>
                  <textarea
                    value={descricaoTemplate}
                    onChange={(e) => setDescricaoTemplate(e.target.value)}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder={t('apreciacoes.ver.modalTemplate.descricaoPlaceholder')}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setMostrarModalTemplate(false);
                    setNomeTemplate('');
                    setDescricaoTemplate('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  {t('comum.acao.cancelar')}
                </button>
                <button
                  onClick={handleCriarTemplate}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {t('apreciacoes.ver.criarTemplate')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
